import {
  CheckinAcceptedResponseSchema,
  CheckoutAcceptedResponseSchema,
  ErrorResponseSchema,
  errorMessageVi,
  EventBootstrapResponseSchema,
  type CheckinRequest,
  type CheckoutRequest,
  type ErrorCode,
  type EventBootstrapResponse,
} from '@irace/shared';

/**
 * Why the outcome is a value rather than a thrown error: the page has to
 * distinguish "this event does not exist" (a final answer — show M01) from
 * "the network failed" (recoverable — show the offline screen and let the
 * participant retry). Collapsing both into an exception loses that.
 */
export type BootstrapResult =
  | { kind: 'ok'; bootstrap: EventBootstrapResponse }
  | { kind: 'not_found' }
  | { kind: 'network_error' }
  | { kind: 'invalid_response' };

export async function fetchBootstrap(slug: string, signal?: AbortSignal): Promise<BootstrapResult> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/events/${encodeURIComponent(slug)}/bootstrap`, {
      headers: { Accept: 'application/json' },
      signal,
    });
  } catch {
    return { kind: 'network_error' };
  }

  if (response.status === 404) return { kind: 'not_found' };
  if (!response.ok) return { kind: 'network_error' };

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { kind: 'invalid_response' };
  }

  const parsed = EventBootstrapResponseSchema.safeParse(payload);
  // A response that does not match the contract is treated as unusable rather
  // than rendered optimistically: a half-parsed form would let a participant
  // submit against a location list the server never sent.
  return parsed.success ? { kind: 'ok', bootstrap: parsed.data } : { kind: 'invalid_response' };
}

/**
 * The answers POST /api/v1/checkins can give a pending record.
 *
 * Two of the codes are the client's own, not the server's:
 *
 * - `NETWORK` stands for "we never heard back", which the caller must treat
 *   exactly like 503: the submission may well have been accepted (§8.4), so
 *   the record stays pending and retries with the same submission_id.
 * - `UNKNOWN_REJECTED` stands for "the API rejected this with a 4xx and a code
 *   this bundle has never heard of". Terminal, for the reasons on
 *   `readErrorEnvelope` below.
 */
export type SubmitOutcome =
  | { kind: 'accepted' }
  | {
      kind: 'error';
      code: ErrorCode | 'NETWORK' | 'UNKNOWN_REJECTED';
      message: string;
      retryAfterSeconds?: number;
    };

const SUBMIT_TIMEOUT_MS = 15_000;

function retryAfterSeconds(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (!header) return undefined;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/**
 * Recognise this API's error envelope without insisting on the code.
 *
 * `ErrorResponseSchema` types `code` as the shipped enum, and that enum is the
 * part of the contract that goes stale: a device runs whatever bundle its
 * service worker cached, so a code added to the API after that bundle shipped
 * fails the parse. Before this existed the failure fell through to the
 * unreadable-body branch and came back as `NETWORK`, which is retryable — so a
 * terminal 4xx rejection became something the device retried every sixty
 * seconds, with a fresh Turnstile challenge each time, forever.
 *
 * When the code cannot be recognised, the HTTP status class is the signal that
 * does not go stale. A 4xx means the request itself is wrong and will be wrong
 * again on the next identical attempt, so stop. A 5xx, like a dropped
 * connection, means try later. Status classes have been stable for thirty
 * years; this enum changes every time the API grows.
 *
 * Deliberately narrower than "any 4xx is terminal": only a body carrying
 * `ok: false` with a string code and message is treated as the API speaking.
 * A 4xx with an HTML or empty body could be an intermediary that never reached
 * the Worker, and that submission really has not been decided yet.
 *
 * Hand-rolled instead of a second Zod schema for the same reason the accepted
 * schema is passed in structurally below — this file does not pull zod in.
 */
function readErrorEnvelope(payload: unknown): { code: string; message: string } | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const body = payload as Record<string, unknown>;
  if (body['ok'] !== false) return null;
  const code: unknown = body['code'];
  const message: unknown = body['message'];
  if (typeof code !== 'string' || typeof message !== 'string') return null;
  return { code, message };
}

/**
 * The one POST both hot-path submissions make.
 *
 * Check-in and check-out share this body rather than each keeping a copy: they
 * must agree on the timeout, on what a 202 means, and above all on treating an
 * unreadable answer as retryable. Two copies would eventually disagree, and
 * the half that stopped retrying would silently drop records.
 */
async function postSubmission(
  path: string,
  request: unknown,
  // Structural rather than `z.ZodType`: only `safeParse` is used here, and the
  // participant bundle does not take a direct dependency on zod for it.
  acceptedSchema: { safeParse: (value: unknown) => { success: boolean } },
  signal?: AbortSignal,
): Promise<SubmitOutcome> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(request),
      // A submit that hangs forever would leave the button spinning past the
      // point where the participant has walked away from the desk.
      signal: signal ?? AbortSignal.timeout(SUBMIT_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'error', code: 'NETWORK', message: errorMessageVi('TEMPORARY_UNAVAILABLE') };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (response.status === 202) {
    // Success is the 202 itself; the body is parsed only to catch a proxy or
    // captive portal that answered on the API's behalf.
    return acceptedSchema.safeParse(payload).success
      ? { kind: 'accepted' }
      : { kind: 'error', code: 'NETWORK', message: errorMessageVi('TEMPORARY_UNAVAILABLE') };
  }

  const retryAfter = retryAfterSeconds(response);
  const parsed = ErrorResponseSchema.safeParse(payload);
  if (parsed.success) {
    return {
      kind: 'error',
      code: parsed.data.code,
      message: parsed.data.message,
      ...(retryAfter !== undefined ? { retryAfterSeconds: retryAfter } : {}),
    };
  }

  const envelope = readErrorEnvelope(payload);
  if (envelope) {
    // Our envelope, a code this bundle predates. Fall back to the status
    // class: 4xx is the request's own fault and will not fix itself, 5xx is
    // the server's and might. The server's message is passed through — it is
    // the only human-readable account of a rejection we cannot name.
    const terminal = response.status >= 400 && response.status < 500;
    return {
      kind: 'error',
      code: terminal ? 'UNKNOWN_REJECTED' : 'NETWORK',
      message: envelope.message,
      ...(retryAfter !== undefined ? { retryAfterSeconds: retryAfter } : {}),
    };
  }

  // An unrecognizable body (HTML error page, gateway 502) says nothing about
  // the submission, so it is treated as "unknown" and stays retryable.
  return {
    kind: 'error',
    code: 'NETWORK',
    message: errorMessageVi('TEMPORARY_UNAVAILABLE'),
    ...(retryAfter !== undefined ? { retryAfterSeconds: retryAfter } : {}),
  };
}

export async function submitCheckin(
  request: CheckinRequest,
  signal?: AbortSignal,
): Promise<SubmitOutcome> {
  return postSubmission('/api/v1/checkins', request, CheckinAcceptedResponseSchema, signal);
}

/**
 * POST /api/v1/checkouts — the departure half of the hot path.
 *
 * `NETWORK` means the same thing here as it does for a check-in: we never
 * heard back, the Queue may well hold the message already, so the record stays
 * pending and the next attempt reuses the same submission_id.
 */
export async function submitCheckout(
  request: CheckoutRequest,
  signal?: AbortSignal,
): Promise<SubmitOutcome> {
  return postSubmission('/api/v1/checkouts', request, CheckoutAcceptedResponseSchema, signal);
}
